import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import SiteEditorRow from "./SiteEditorRow";

// Manage Sites (slice A6). Implements the eight required interaction fixes in design §8.3, each of
// which corrects a specific defect in the classic site grid that suppressed adoption of the grain:
//   1 MUI Dialog                     5 synthetic Site 0 never shown as saved (realSites() upstream)
//   2 genuine neutral empty state    6 new site visually distinct from persisted ones
//   3 explicit + Add Site            7 no Delete on an unsaved site
//   4 no editor until Add is pressed 8 API errors surfaced, incl. 200-with-an-error-string
//
// Read-first inside the dialog too: persisted sites render as read-only rows until Edit is pressed.
export default function SiteManagementDialog(props) {
  const open = props.open;
  const onClose = props.onClose;
  const facility = props.facility;
  const onSaveSite = props.onSaveSite;
  const onDeleteSite = props.onDeleteSite;
  const onSaved = props.onSaved;

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reset transient UI state on every open so a reopen never resumes a half-finished edit.
  useEffect(() => {
    if (!open) return;
    setAdding(false);
    setEditingId(null);
    setConfirmDeleteId(null);
    setError(null);
  }, [open]);

  // Already excludes the synthetic facility_site_id === 0 placeholder (franchiseAdminModel).
  const sites = facility.sites;

  const runMutation = async (action, successMessage) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      if (onSaved) onSaved(successMessage);
      setAdding(false);
      setEditingId(null);
      setConfirmDeleteId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSite = (payload) =>
    runMutation(
      () =>
        onSaveSite({
          ...payload,
          facility_id: facility.id,
          facility_source: facility.source,
        }),
      payload.facility_site_id === 0
        ? `Site "${payload.facilityName}" added`
        : `Site "${payload.facilityName}" updated`
    );

  const handleDeleteSite = (site) =>
    runMutation(() => onDeleteSite(site.facility_site_id), `Site "${site.facility}" deleted`);

  const siteRows = sites.map((site) => {
    if (editingId === site.facility_site_id) {
      return (
        <TableRow key={site.facility_site_id}>
          <TableCell colSpan={6} sx={{ p: 1, border: 0 }}>
            <SiteEditorRow
              site={site}
              saving={busy}
              onSave={handleSaveSite}
              onCancel={() => setEditingId(null)}
            />
          </TableCell>
        </TableRow>
      );
    }

    const confirming = confirmDeleteId === site.facility_site_id;

    return (
      <TableRow key={site.facility_site_id} hover>
        <TableCell>{site.facility}</TableCell>
        <TableCell align="center">{site.numFields ?? 0}</TableCell>
        <TableCell align="center">{site.space ?? "—"}</TableCell>
        <TableCell align="center">
          {site.indooravailable === 1 ? "Indoor" : ""}
          {site.indooravailable === 1 && site.outdooravailable === 1 ? " · " : ""}
          {site.outdooravailable === 1 ? "Outdoor" : ""}
          {site.indooravailable !== 1 && site.outdooravailable !== 1 ? "—" : ""}
        </TableCell>
        <TableCell align="center">
          {site.facilityactive === 1 ? (
            <Chip label="Active" size="small" variant="outlined" color="success" />
          ) : (
            <Chip label="Inactive" size="small" variant="outlined" sx={{ color: "text.secondary", borderColor: "divider" }} />
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          {confirming ? (
            // Two-step delete. The confirm sits in its own position and is never where a save
            // button would be -- the classic grid swapped Add/Update and Delete into one cell.
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", justifyContent: "flex-end" }}>
              <Typography variant="caption" color="text.secondary">Delete?</Typography>
              <Button size="small" color="inherit" onClick={() => setConfirmDeleteId(null)} disabled={busy}>
                Cancel
              </Button>
              <Button size="small" color="error" variant="contained" onClick={() => handleDeleteSite(site)} disabled={busy}>
                Delete
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() => { setEditingId(site.facility_site_id); setAdding(false); }}
                disabled={busy}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => setConfirmDeleteId(site.facility_site_id)}
                disabled={busy}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Delete
              </Button>
            </Box>
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{facility.name} — Sites</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Physical sites for this facility — fields, space and indoor/outdoor availability.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {sites.length === 0 ? (
          // Requirement 2: a real empty state, worded neutrally. Having no sites is normal.
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No sites configured
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Site name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Fields</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Space</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Availability</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                {/* Reserve room for the WIDEST state this column reaches -- the two-step delete
                    confirmation ("Delete?" + Cancel + Delete, ~178px). Sizing it to the resting
                    Edit/Delete state instead lets the confirmation overflow left into Status;
                    flexbox start-direction overflow does not register in scrollWidth, so it is
                    invisible to the usual clipping checks. */}
                <TableCell align="right" sx={{ fontWeight: 600, width: 200, minWidth: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{siteRows}</TableBody>
          </Table>
        )}

        <Box sx={{ mt: 2 }}>
          {adding ? (
            <SiteEditorRow
              site={null}
              saving={busy}
              onSave={handleSaveSite}
              onCancel={() => setAdding(false)}
            />
          ) : (
            // Requirement 3/4: the ONLY way to get an editable row is to press this.
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setAdding(true); setEditingId(null); }}
              disabled={busy}
              sx={{ textTransform: "none" }}
            >
              + Add Site
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
